import type { MyPlanCardDto, MyPlanStageDto, MyPlanTaskDto } from '../../lib/api/openingTasks';
import { partnerDatesText } from '../opening-tasks/stage-tasks';

/**
 * Чистая модель диаграммы Ганта плана открытия для партнёра (вики, визард «Открытие точки»,
 * владелец 31.10.2026): диапазон и шкала времени, перевод дат в пиксели, разметка шапки,
 * вертикали («Сегодня», «Аренда», «Тех. открытие», «Официальное открытие»), группировка задач
 * по этапам с фильтрами, тексты подсказок. Без React — покрыто `plan-gantt-model.test.ts`.
 *
 * Все даты — календарные строки `YYYY-MM-DD`. Считаем в UTC-днях (`Date.UTC`), без часовых
 * поясов и без `new Date()` с локальным временем: на проде сервер в UTC, у партнёра — свой пояс.
 */

// ───────────────────────── Масштаб ─────────────────────────

export type GanttScale = 'day' | 'week' | 'month';

/** Порядок чипов масштаба. */
export const GANTT_SCALES: readonly GanttScale[] = ['day', 'week', 'month'];

export const GANTT_SCALE_LABEL: Record<GanttScale, string> = { day: 'Дни', week: 'Недели', month: 'Месяцы' };

/** Масштаб по умолчанию (владелец: «Недели»). */
export const DEFAULT_SCALE: GanttScale = 'week';

/** Ширина одного дня в пикселях: неделя — 119 px, месяц ≈ 150 px. */
export const PX_PER_DAY: Record<GanttScale, number> = { day: 28, week: 17, month: 5 };

/** Больше трёх лет шкалу не растягиваем (сопровождение тянется на полгода — оно в диапазон не входит). */
export const MAX_DAYS = 1100;

/** Запас шкалы: слева 3 дня, справа 7 (потом границы подгоняются под масштаб). */
export const PAD_BEFORE_DAYS = 3;
export const PAD_AFTER_DAYS = 7;

// ───────────────────────── Даты (YYYY-MM-DD) ─────────────────────────

const DAY_MS = 86_400_000;
const YMD_RE = /^(\d{4})-(\d{2})-(\d{2})/;

function utcMs(ymd: string): number {
  const m = YMD_RE.exec(ymd);
  return m ? Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : NaN;
}

function ymdFromMs(ms: number): string {
  const d = new Date(ms);
  return `${String(d.getUTCFullYear()).padStart(4, '0')}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
}

/**
 * Календарная дата из строки: берёт первые 10 символов (`2026-11-05T00:00:00Z` тоже подойдёт) и
 * проверяет, что такой день существует (31 февраля — не дата). Иначе null.
 */
export function normYmd(v: unknown): string | null {
  if (typeof v !== 'string') return null;
  const m = YMD_RE.exec(v);
  if (!m) return null;
  const s = `${m[1]}-${m[2]}-${m[3]}`;
  const ms = utcMs(s);
  return Number.isNaN(ms) || ymdFromMs(ms) !== s ? null : s;
}

export function ymdAdd(ymd: string, n: number): string {
  return ymdFromMs(utcMs(ymd) + n * DAY_MS);
}

/** b − a в днях (целое). */
export function ymdDiff(a: string, b: string): number {
  return Math.round((utcMs(b) - utcMs(a)) / DAY_MS);
}

/** 0 = воскресенье … 6 = суббота. */
export function weekdayOf(ymd: string): number {
  return new Date(utcMs(ymd)).getUTCDay();
}

/** Понедельник той недели, в которую попадает дата. */
export function mondayOf(ymd: string): string {
  return ymdAdd(ymd, -((weekdayOf(ymd) + 6) % 7));
}

export function monthStartOf(ymd: string): string {
  return `${ymd.slice(0, 7)}-01`;
}

export function monthEndOf(ymd: string): string {
  const y = Number(ymd.slice(0, 4));
  const mo = Number(ymd.slice(5, 7));
  return ymdFromMs(Date.UTC(y, mo, 0));
}

const MONTH_NOM = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];
const MONTH_SHORT = ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];

export const monthName = (ymd: string): string => MONTH_NOM[Number(ymd.slice(5, 7)) - 1] ?? '';
export const monthShort = (ymd: string): string => MONTH_SHORT[Number(ymd.slice(5, 7)) - 1] ?? '';

/** «16.10» — день и месяц (год понятен из контекста). */
export function fmtDM(ymd: string | null | undefined, placeholder = '—'): string {
  const d = normYmd(ymd);
  return d ? `${d.slice(8, 10)}.${d.slice(5, 7)}` : placeholder;
}

// ───────────────────────── Диапазон шкалы ─────────────────────────

export interface DateRange {
  /** Первый день шкалы. */
  start: string;
  /** Последний день шкалы (включительно). */
  end: string;
  days: number;
}

/**
 * Диапазон шкалы по набору дат: от самой ранней − запас до самой поздней + запас, границы
 * подгоняются под масштаб (дни и недели — по неделям Пн–Вс, месяцы — по границам месяцев).
 * Мусорные даты отбрасываются; если годных нет — берём `fallback`. Длина не больше `maxDays`
 * (лишнее справа обрезается).
 */
export function computeRange(dates: ReadonlyArray<string | null | undefined>, scale: GanttScale, fallback: string, maxDays = MAX_DAYS): DateRange {
  const valid = dates.map(normYmd).filter((d): d is string => d !== null).sort();
  const base = normYmd(fallback) ?? '1970-01-01';
  const min = valid[0] ?? base;
  const max = valid[valid.length - 1] ?? base;
  const lo = ymdAdd(min, -PAD_BEFORE_DAYS);
  const hi = ymdAdd(max, PAD_AFTER_DAYS);
  const start = scale === 'month' ? monthStartOf(lo) : mondayOf(lo);
  let end = scale === 'month' ? monthEndOf(hi) : ymdAdd(mondayOf(hi), 6);
  let days = ymdDiff(start, end) + 1;
  if (days > maxDays) {
    days = maxDays;
    end = ymdAdd(start, days - 1);
  }
  return { start, end, days };
}

/** Какие даты карточки определяют длину шкалы. Сопровождение (фаза `support`) тянется на полгода — не берём. */
export function planRangeDates(card: Pick<MyPlanCardDto, 'today' | 'lease' | 'techOpen' | 'officialOpen' | 'stages' | 'tasks'>): string[] {
  const out: string[] = [card.today, card.lease.date, card.techOpen.date, card.officialOpen.date];
  const supportCodes = new Set<string>();
  for (const s of card.stages) {
    if (s.phaseCode === 'support') {
      supportCodes.add(s.code);
      continue;
    }
    out.push(s.start, s.end);
  }
  for (const t of card.tasks) {
    if (t.ganttStatus === 'skipped' || supportCodes.has(t.stageCode)) continue;
    if (t.startDate) out.push(t.startDate);
    if (t.dueDate) out.push(t.dueDate);
  }
  return out;
}

// ───────────────────────── Дата → пиксели ─────────────────────────

/** Левый край дня на шкале, px. Дата левее начала даёт отрицательное значение. */
export function dateToX(range: Pick<DateRange, 'start'>, ymd: string, ppd: number): number {
  return ymdDiff(range.start, ymd) * ppd;
}

export interface DateSpan {
  start: string;
  end: string;
}

/**
 * Окно бара задачи. Обе даты — как есть (если перепутаны, меняем местами); только срок или
 * только начало — бар в один день; ни одной даты — null (бар не рисуем, строка остаётся).
 */
export function taskSpan(t: { startDate: string | null; dueDate: string | null }): DateSpan | null {
  const s = normYmd(t.startDate);
  const d = normYmd(t.dueDate);
  if (!s && !d) return null;
  const a = s ?? (d as string);
  const b = d ?? (s as string);
  return a <= b ? { start: a, end: b } : { start: b, end: a };
}

export interface BarRect {
  left: number;
  width: number;
  /** Окно начинается левее шкалы и подрезано. */
  clippedLeft: boolean;
  /** Окно кончается правее шкалы и подрезано (бар «растворяется» справа). */
  clippedRight: boolean;
}

/**
 * Прямоугольник бара на шкале: окно включительно, подрезается по краям шкалы; целиком вне шкалы — null.
 * Ширина не меньше `minWidth`, чтобы однодневный бар на масштабе «Месяцы» не исчезал.
 */
export function barRect(range: DateRange, ppd: number, span: DateSpan, minWidth = 6): BarRect | null {
  if (span.end < range.start || span.start > range.end) return null;
  const clippedLeft = span.start < range.start;
  const clippedRight = span.end > range.end;
  const s = clippedLeft ? range.start : span.start;
  const e = clippedRight ? range.end : span.end;
  return {
    left: dateToX(range, s, ppd),
    width: Math.max((ymdDiff(s, e) + 1) * ppd, minWidth),
    clippedLeft,
    clippedRight,
  };
}

/** Центр дня на шкале (для вех и вертикалей), px. */
export function dateCenterX(range: Pick<DateRange, 'start'>, ymd: string, ppd: number): number {
  return dateToX(range, ymd, ppd) + ppd / 2;
}

/**
 * Куда прокрутить шкалу при открытии, чтобы «сегодня» было на виду: на треть видимой ширины
 * от левого края (но не дальше 160 px), чтобы слева остался вчерашний день.
 */
export function scrollTargetLeft(todayX: number, visibleWidth: number): number {
  const margin = Math.min(160, Math.max(0, visibleWidth) / 3);
  return Math.max(0, Math.round(todayX - margin));
}

// ───────────────────────── Шапка шкалы ─────────────────────────

export interface HeaderCell {
  key: string;
  x: number;
  w: number;
  text: string;
  /** Суббота / воскресенье (масштаб «Дни»). */
  weekend?: boolean;
  /** Это сегодняшний день / неделя / месяц. */
  today?: boolean;
}

export interface ScaleHeader {
  /** Верхний ярус: месяцы (дни, недели) или годы (месяцы). */
  top: HeaderCell[];
  /** Нижний ярус: дни, недели (по понедельникам) или месяцы. */
  bottom: HeaderCell[];
}

/** Подпись верхнего яруса по ширине ячейки: узкому остатку месяца хватает трёх букв. */
function topText(full: string, short: string, w: number): string {
  if (w >= 110) return full;
  if (w >= 34) return short;
  return '';
}

/** Месяцы (или годы) подряд в диапазоне: по дню считаем, где меняется ключ. */
function segmentsBy(range: DateRange, ppd: number, keyLen: 4 | 7): Array<{ from: string; days: number; x: number }> {
  const out: Array<{ from: string; days: number; x: number }> = [];
  let from = range.start;
  let fromIdx = 0;
  for (let i = 1; i <= range.days; i++) {
    const cur = i < range.days ? ymdAdd(range.start, i) : null;
    const prev = ymdAdd(range.start, i - 1);
    if (cur === null || cur.slice(0, keyLen) !== prev.slice(0, keyLen)) {
      out.push({ from, days: i - fromIdx, x: fromIdx * ppd });
      if (cur !== null) {
        from = cur;
        fromIdx = i;
      }
    }
  }
  return out;
}

export function buildScaleHeader(range: DateRange, scale: GanttScale, today: string): ScaleHeader {
  const ppd = PX_PER_DAY[scale];
  const top: HeaderCell[] = [];
  const bottom: HeaderCell[] = [];

  if (scale === 'month') {
    for (const seg of segmentsBy(range, ppd, 4)) {
      const w = seg.days * ppd;
      top.push({ key: `y${seg.from.slice(0, 4)}`, x: seg.x, w, text: seg.from.slice(0, 4) });
    }
    for (const seg of segmentsBy(range, ppd, 7)) {
      const w = seg.days * ppd;
      bottom.push({ key: `m${seg.from.slice(0, 7)}`, x: seg.x, w, text: monthShort(seg.from), today: today.slice(0, 7) === seg.from.slice(0, 7) });
    }
    return { top, bottom };
  }

  for (const seg of segmentsBy(range, ppd, 7)) {
    const w = seg.days * ppd;
    top.push({ key: `mo${seg.from.slice(0, 7)}`, x: seg.x, w, text: topText(`${monthName(seg.from)} ${seg.from.slice(0, 4)}`, monthShort(seg.from), w) });
  }

  if (scale === 'week') {
    const weekOfToday = mondayOf(today);
    for (let i = 0; i < range.days; i += 7) {
      const d = ymdAdd(range.start, i);
      bottom.push({ key: `w${d}`, x: i * ppd, w: Math.min(7, range.days - i) * ppd, text: fmtDM(d), today: d === weekOfToday });
    }
  } else {
    for (let i = 0; i < range.days; i++) {
      const d = ymdAdd(range.start, i);
      const wd = weekdayOf(d);
      bottom.push({ key: `d${d}`, x: i * ppd, w: ppd, text: String(Number(d.slice(8, 10))), weekend: wd === 0 || wd === 6, today: d === today });
    }
  }
  return { top, bottom };
}

// ───────────────────────── Вертикали ─────────────────────────

export type MarkerKind = 'today' | 'lease' | 'tech' | 'official';

export interface PlanMarker {
  kind: MarkerKind;
  date: string;
  /** «Аренда ≈ 12.10». */
  label: string;
  /** Дата — прогноз (≈): вертикаль бледнее, ромб пустой. */
  estimated: boolean;
  /** Центр дня, px от начала шкалы. */
  x: number;
  /** Ярус подписи в шапке (0 — верхний). */
  lane: number;
  /** Оценка ширины подписи, px — для раскладки по ярусам. */
  labelWidth: number;
}

const MARKER_TITLE: Record<MarkerKind, string> = {
  today: 'Сегодня',
  lease: 'Аренда',
  tech: 'Тех. открытие',
  official: 'Официальное открытие',
};

/** Подпись вертикали: «Сегодня 31.10», «Аренда ≈ 12.10». */
export function markerLabel(kind: MarkerKind, date: string, estimated: boolean): string {
  return `${MARKER_TITLE[kind]}${estimated ? ' ≈' : ''} ${fmtDM(date)}`;
}

/** Ширина подписи на глаз: 11 px шрифт ≈ 6.2 px на знак, плюс ромб и отступ. */
export function estimateLabelWidth(label: string): number {
  return Math.round(label.length * 6.2 + 18);
}

/**
 * Ярусы подписей: подпись идёт вправо от своей вертикали; следующая по порядку ложится на первый
 * ярус, где предыдущая уже закончилась (с зазором `gap`). Результат — ярус для каждого входа
 * в исходном порядке.
 */
export function assignLanes(items: ReadonlyArray<{ x: number; width: number }>, gap = 6): number[] {
  const order = items.map((_, i) => i).sort((a, b) => items[a]!.x - items[b]!.x || a - b);
  const laneEnd: number[] = [];
  const lanes = new Array<number>(items.length).fill(0);
  for (const i of order) {
    const it = items[i]!;
    let lane = 0;
    while (laneEnd[lane] !== undefined && laneEnd[lane]! + gap > it.x) lane += 1;
    lanes[i] = lane;
    laneEnd[lane] = it.x + it.width;
  }
  return lanes;
}

/** Вертикали карточки в пределах шкалы, с раскладкой подписей по ярусам. */
export function buildMarkers(
  card: Pick<MyPlanCardDto, 'today' | 'lease' | 'techOpen' | 'officialOpen'>,
  range: DateRange,
  ppd: number,
): PlanMarker[] {
  const raw: Array<{ kind: MarkerKind; date: string; estimated: boolean }> = [
    { kind: 'today', date: card.today, estimated: false },
    { kind: 'lease', date: card.lease.date, estimated: card.lease.estimated },
    { kind: 'tech', date: card.techOpen.date, estimated: card.techOpen.estimated },
    { kind: 'official', date: card.officialOpen.date, estimated: card.officialOpen.estimated },
  ];
  const placed: Array<Omit<PlanMarker, 'lane'>> = [];
  for (const m of raw) {
    const date = normYmd(m.date);
    if (!date || date < range.start || date > range.end) continue;
    const label = markerLabel(m.kind, date, m.estimated);
    placed.push({ kind: m.kind, date, label, estimated: m.estimated, x: dateCenterX(range, date, ppd), labelWidth: estimateLabelWidth(label) });
  }
  const lanes = assignLanes(placed.map((p) => ({ x: p.x, width: p.labelWidth })));
  return placed.map((p, i) => ({ ...p, lane: lanes[i]! })).sort((a, b) => a.x - b.x);
}

// ───────────────────────── Статусы и цвета ─────────────────────────

export type PlanGanttStatus = MyPlanTaskDto['ganttStatus'];

/** Статусы, которые показываем в легенде и на барах (пропущенные в плане партнёра не приходят). */
export const GANTT_LEGEND_STATUSES: readonly PlanGanttStatus[] = ['done', 'in_progress', 'rework', 'blocked', 'plan', 'forecast'];

export const GANTT_STATUS_LABEL: Record<PlanGanttStatus, string> = {
  done: 'Готово',
  in_progress: 'В работе',
  rework: 'Доработка',
  blocked: 'Зависит',
  plan: 'План',
  forecast: 'Прогноз',
  skipped: 'Пропущено',
};

/** Цвет бара задачи, hex (те же, что в карточке открытия CRM). */
export const GANTT_STATUS_COLOR: Record<PlanGanttStatus, string> = {
  done: '#7CB342',
  rework: '#FB8C00',
  in_progress: '#0096FF',
  blocked: '#8B92A8',
  forecast: '#C8CCD8',
  plan: '#9FC5F8',
  skipped: '#E5E8F0',
};

/** Цвет фазы пути открытия — полоса этапа. */
export const PHASE_COLOR: Record<string, string> = {
  preparation: '#8B92A8',
  premises: '#0096FF',
  construction: '#FF6900',
  launch: '#7CB342',
  opening: '#251D4A',
  support: '#00A6A6',
};

const PHASE_FALLBACK = '#8B92A8';

export const phaseColor = (code: string | null | undefined): string => (code && PHASE_COLOR[code]) || PHASE_FALLBACK;

/** #rrggbb + прозрачность → rgba(); не-hex возвращается как есть. */
export function withAlpha(hex: string, alpha: number): string {
  const m = /^#([0-9a-f]{6})$/i.exec(hex);
  if (!m) return hex;
  const n = parseInt(m[1]!, 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${alpha})`;
}

export const STAGE_STATE_LABEL: Record<MyPlanStageDto['state'], string> = { done: 'Пройден', current: 'Идёт сейчас', future: 'Впереди' };

/** Короткая подпись состояния этапа в левой колонке. */
export function stageStateText(stage: Pick<MyPlanStageDto, 'state' | 'estimated'>): string {
  if (stage.state === 'done') return 'Готово';
  if (stage.state === 'current') return 'Идёт';
  return stage.estimated ? 'Прогноз' : 'План';
}

// ───────────────────────── Фильтры и группировка ─────────────────────────

/** Чипы: все задачи / задачи партнёра («Мои») / задачи УК. */
export type SideFilter = 'all' | 'partner' | 'uk';

export const SIDE_FILTERS: readonly SideFilter[] = ['all', 'partner', 'uk'];

export const SIDE_FILTER_LABEL: Record<SideFilter, string> = { all: 'Все задачи', partner: 'Мои задачи', uk: 'Задачи УК' };

export interface PlanFilter {
  side: SideFilter;
  onlyOpen: boolean;
}

export const NO_FILTER: PlanFilter = { side: 'all', onlyOpen: false };

export const isFilterActive = (f: PlanFilter): boolean => f.side !== 'all' || f.onlyOpen;

/** Задача УК — только сторона `uk`; «общие» (`both`) для партнёра свои. */
export const isUkTask = (t: Pick<MyPlanTaskDto, 'side'>): boolean => t.side === 'uk';

/** Задача уже выполнена. */
export const isTaskDone = (t: Pick<MyPlanTaskDto, 'done' | 'ganttStatus'>): boolean => t.done || t.ganttStatus === 'done';

/** Пропущенные в плане не показываем никогда (сервер их и не присылает). */
export const isTaskHidden = (t: Pick<MyPlanTaskDto, 'ganttStatus'>): boolean => t.ganttStatus === 'skipped';

export function taskMatchesFilter(t: Pick<MyPlanTaskDto, 'side' | 'done' | 'ganttStatus'>, f: PlanFilter): boolean {
  if (isTaskHidden(t)) return false;
  if (f.side === 'partner' && isUkTask(t)) return false;
  if (f.side === 'uk' && !isUkTask(t)) return false;
  if (f.onlyOpen && isTaskDone(t)) return false;
  return true;
}

/** Сколько задач под каждый чип стороны — с учётом «Только открытые», но без самой стороны. */
export function sideCounts(tasks: ReadonlyArray<Pick<MyPlanTaskDto, 'side' | 'done' | 'ganttStatus'>>, onlyOpen: boolean): Record<SideFilter, number> {
  const out: Record<SideFilter, number> = { all: 0, partner: 0, uk: 0 };
  for (const t of tasks) {
    if (isTaskHidden(t) || (onlyOpen && isTaskDone(t))) continue;
    out.all += 1;
    if (isUkTask(t)) out.uk += 1;
    else out.partner += 1;
  }
  return out;
}

export interface StageGroup {
  stage: MyPlanStageDto;
  /** Задачи этапа, прошедшие фильтр, в порядке плана. */
  tasks: MyPlanTaskDto[];
  /** Этапа нет в справочнике этапов карточки: задачи собраны в «Другие задачи». */
  orphan: boolean;
}

/** Код служебного этапа для задач, чей этап в плане не найден. */
export const ORPHAN_STAGE_CODE = '__other';

/**
 * Этапы карточки с их задачами под фильтром. Порядок этапов и задач — как прислал сервер. Пока
 * фильтр включён, этапы без подходящих задач скрыты (иначе «Только открытые» показывало бы
 * десяток пустых строк); без фильтра этап виден всегда, даже без задач. Задачи с неизвестным
 * этапом не теряем — отдельной группой в конце.
 */
export function groupTasksByStage(
  stages: readonly MyPlanStageDto[],
  tasks: readonly MyPlanTaskDto[],
  filter: PlanFilter,
  today: string,
): StageGroup[] {
  const known = new Set(stages.map((s) => s.code));
  const byStage = new Map<string, MyPlanTaskDto[]>();
  const orphansAll: MyPlanTaskDto[] = [];
  const orphansShown: MyPlanTaskDto[] = [];
  for (const t of tasks) {
    if (isTaskHidden(t)) continue;
    const match = taskMatchesFilter(t, filter);
    if (!known.has(t.stageCode)) {
      orphansAll.push(t);
      if (match) orphansShown.push(t);
      continue;
    }
    if (!match) continue;
    const list = byStage.get(t.stageCode);
    if (list) list.push(t);
    else byStage.set(t.stageCode, [t]);
  }
  const active = isFilterActive(filter);
  const out: StageGroup[] = [];
  for (const stage of stages) {
    const list = byStage.get(stage.code) ?? [];
    if (active && list.length === 0) continue;
    out.push({ stage, tasks: list, orphan: false });
  }
  if (orphansShown.length > 0) out.push({ stage: orphanStage(orphansAll, today), tasks: orphansShown, orphan: true });
  return out;
}

/** Служебный этап для задач без этапа: окно — от самой ранней до самой поздней даты этих задач. */
function orphanStage(orphans: readonly MyPlanTaskDto[], today: string): MyPlanStageDto {
  const dates = orphans.flatMap((t) => [normYmd(t.startDate), normYmd(t.dueDate)]).filter((d): d is string => d !== null).sort();
  return {
    code: ORPHAN_STAGE_CODE,
    label: 'Другие задачи',
    phaseCode: null,
    deptLabel: null,
    start: dates[0] ?? today,
    end: dates[dates.length - 1] ?? today,
    estimated: false,
    state: 'future',
    tasksDone: orphans.filter(isTaskDone).length,
    tasksTotal: orphans.length,
  };
}

/**
 * Какие этапы раскрыты. `manual` — выбор человека (кнопки, «Развернуть все»), он главнее всего.
 * Иначе: без фильтра раскрыт текущий этап (нет текущего — ближайший будущий), с фильтром —
 * все этапы, где что-то нашлось (иначе «Задачи УК» показывали бы свёрнутый список).
 */
export function effectiveExpanded(groups: readonly StageGroup[], manual: ReadonlySet<string> | null, filterActive: boolean): Set<string> {
  if (manual) return new Set(manual);
  if (filterActive) return new Set(groups.filter((g) => g.tasks.length > 0).map((g) => g.stage.code));
  const cur = groups.find((g) => g.stage.state === 'current') ?? groups.find((g) => g.stage.state === 'future');
  return new Set(cur ? [cur.stage.code] : []);
}

/** Этап можно раскрыть, только если в нём есть что показать. */
export const isExpandable = (g: StageGroup): boolean => g.tasks.length > 0;

/** Коды всех раскрываемых этапов — для «Развернуть все». */
export const expandableCodes = (groups: readonly StageGroup[]): string[] => groups.filter(isExpandable).map((g) => g.stage.code);

/** Все раскрываемые этапы уже раскрыты — кнопка превращается в «Свернуть все». */
export function allExpanded(groups: readonly StageGroup[], expanded: ReadonlySet<string>): boolean {
  const codes = expandableCodes(groups);
  return codes.length > 0 && codes.every((c) => expanded.has(c));
}

// ───────────────────────── Строки диаграммы ─────────────────────────

export type PlanRow =
  | { kind: 'stage'; key: string; group: StageGroup; open: boolean }
  | { kind: 'task'; key: string; task: MyPlanTaskDto; stage: MyPlanStageDto };

/** Плоский список строк: этап, под ним — его задачи, если этап раскрыт. */
export function buildRows(groups: readonly StageGroup[], expanded: ReadonlySet<string>): PlanRow[] {
  const rows: PlanRow[] = [];
  for (const g of groups) {
    const open = isExpandable(g) && expanded.has(g.stage.code);
    rows.push({ kind: 'stage', key: `s:${g.stage.code}`, group: g, open });
    if (open) for (const t of g.tasks) rows.push({ kind: 'task', key: `t:${t.id}`, task: t, stage: g.stage });
  }
  return rows;
}

// ───────────────────────── «Можно делать на этой неделе» (FB-32) ─────────────────────────

/** Поля задачи, нужные для проверки «можно делать на этой неделе». */
export type DoableTask = Pick<MyPlanTaskDto, 'id' | 'side' | 'done' | 'ganttStatus' | 'startDate' | 'dueDate' | 'dependsOn'>;

/** Воскресенье той недели (понедельник — воскресенье), в которую попадает дата. */
export function sundayOf(ymd: string): string {
  return ymdAdd(mondayOf(ymd), 6);
}

/**
 * Можно ли партнёру браться за задачу на этой неделе: не выполнена и не пропущена, не «только УК», все
 * предшественники (`dependsOn`) выполнены, а плановое начало не позже воскресенья текущей недели (просроченное
 * начало — тоже «можно»). `today` — сегодняшний день по Москве, `YYYY-MM-DD` (у карточки это `card.today`).
 * Предшественник, которого нет среди задач плана (его пропустили / удалили), не блокирует. Нет `startDate` —
 * берём срок; нет ни того ни другого — не подсвечиваем (нечего ставить «на неделю»).
 */
export function isDoableThisWeek(task: DoableTask, byId: ReadonlyMap<string, DoableTask>, today: string): boolean {
  if (isUkTask(task) || isTaskDone(task) || task.ganttStatus === 'skipped') return false;
  const start = normYmd(task.startDate) ?? normYmd(task.dueDate);
  const day = normYmd(today);
  if (!start || !day) return false;
  if (start > sundayOf(day)) return false;
  return (task.dependsOn ?? []).every((id) => {
    const dep = byId.get(id);
    return !dep || isTaskDone(dep);
  });
}

/** Множество id задач плана, которые «можно делать на этой неделе». */
export function doableThisWeekIds(tasks: ReadonlyArray<DoableTask>, today: string): Set<string> {
  const byId = new Map(tasks.map((t) => [t.id, t] as const));
  return new Set(tasks.filter((t) => isDoableThisWeek(t, byId, today)).map((t) => t.id));
}

export const DOABLE_THIS_WEEK_LABEL = 'Можно на этой неделе';

// ───────────────────────── Тексты ─────────────────────────

/** «с 12.10 по 16.10», «срок 16.10», «16.10» или «без срока». */
export function taskDatesText(t: { startDate: string | null; dueDate: string | null }): string {
  const s = normYmd(t.startDate);
  const d = normYmd(t.dueDate);
  if (!s && !d) return 'без срока';
  if (s && d) return s === d ? fmtDM(s) : `с ${fmtDM(s)} по ${fmtDM(d)}`;
  return d ? `срок ${fmtDM(d)}` : `с ${fmtDM(s)}`;
}

/** Окно этапа: «с 12.10 по 28.10» (+ « · прогноз»). */
export function stageDatesText(stage: Pick<MyPlanStageDto, 'start' | 'end' | 'estimated'>): string {
  const base = stage.start === stage.end ? fmtDM(stage.start) : `с ${fmtDM(stage.start)} по ${fmtDM(stage.end)}`;
  return stage.estimated ? `${base} · прогноз` : base;
}

/** Кто исполняет задачу — по-русски. */
export function taskSideText(t: Pick<MyPlanTaskDto, 'side' | 'deptLabel'>): string | null {
  const dept = t.deptLabel ? ` · ${t.deptLabel}` : '';
  if (t.side === 'uk') return `Исполняет УК${dept}`;
  if (t.side === 'both') return `Партнёр и УК${dept}`;
  return null;
}

/** «С этапа «Аренда»» для хвоста; null — задача на своём этапе. */
export function carriedText(t: Pick<MyPlanTaskDto, 'carriedFromStage' | 'carriedFromLabel'>): string | null {
  if (t.carriedFromLabel) return `с этапа «${t.carriedFromLabel}»`;
  return t.carriedFromStage ? 'с прошлого этапа' : null;
}

/**
 * Строки подсказки на баре задачи (первая — название). Они же — `aria-label`: цвет статуса
 * всегда продублирован словами.
 */
export function taskTipLines(t: MyPlanTaskDto, stageLabel: string, doable = false): string[] {
  const lines = [t.title, taskDatesText(t), `Статус: ${GANTT_STATUS_LABEL[t.ganttStatus]}`];
  // FB-32 шаг 2: даты, которые партнёр ввёл сам, — только для информации, бар и план они не двигают.
  const own = partnerDatesText(t);
  if (own) lines.push(`Ваши даты: ${own}`);
  if (t.attention === 'overdue' && !isTaskDone(t)) lines.push('Просрочено');
  else if (t.attention === 'soon' && !isTaskDone(t)) lines.push('Срок скоро');
  if (doable) lines.push(DOABLE_THIS_WEEK_LABEL);
  if (t.isMilestone) lines.push('Веха');
  const side = taskSideText(t);
  if (side) lines.push(side);
  const carried = carriedText(t);
  if (carried) lines.push(carried.charAt(0).toUpperCase() + carried.slice(1));
  lines.push(`Этап: ${stageLabel}`);
  return lines;
}

/** Строки подсказки на баре этапа. */
export function stageTipLines(stage: MyPlanStageDto, phaseLabel: string | null): string[] {
  const lines = [stage.label, stageDatesText(stage), STAGE_STATE_LABEL[stage.state]];
  if (phaseLabel) lines.push(`Фаза: ${phaseLabel}`);
  if (stage.deptLabel) lines.push(`Отдел: ${stage.deptLabel}`);
  if (stage.tasksTotal > 0) lines.push(`Задач выполнено: ${stage.tasksDone} из ${stage.tasksTotal}`);
  return lines;
}
